import { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { STUDIO_RELATIONSHIP } from '../../../lib/aiTools.js'
import { findChampionships, resolveSchool, searchSchools, forgetUniverse } from './conceptSources.js'
import {
  CONCEPT_PROMISE,
  SOURCE_BRAND_KIT,
  SOURCE_KEYED,
  applyReceipt,
  composeConcept,
  conceptBlockers,
  parseBrief,
  resolveMatch,
} from './concept.js'
import ConceptCrest from './ConceptCrest.jsx'
import styles from './ConceptBrief.module.css'

/**
 * THE GENERATED CONCEPT — one line, one confirm.
 *
 * Brian: *"A generated concept... a championship ring for Duke Basketball 2027
 * ACC Champions — without any real inputs or logos and it sources all the data
 * we need and builds based off of what we've taught it."*
 *
 * So this is deliberately ONE input and a confirm, not thirteen steps with a
 * flag flipped. The whole design constraint is that the operator's job here is
 * to CHECK, never to fill in.
 *
 * ⚠️ THE RECEIPT IS THE FEATURE, NOT THE DECORATION. Sourcing without a visible
 * receipt is how the wrong school ends up on a ring — a rep would have no way
 * to tell a good match from a confident bad one. So every line the system took
 * is on screen with the record it came from, before anything renders, and every
 * line is editable. `concept.js` composes; this component only displays and
 * hands corrections back through `applyReceipt`.
 *
 * ⚠️ AN AMBIGUOUS BRIEF ASKS ONE QUESTION. It does not fall back to the
 * thirteen steps — that would be the level collapsing into the other level,
 * which is the exact defect this build exists to fix. `resolveMatch()` returns
 * `choose` and the operator picks a record from a list. One click, then on.
 *
 * NO HUBSPOT FROM THE BROWSER (hard rule #2): both lookups go through
 * `lib/api.js` to the JWT backend — `findChampionships` → `/api/championships/all`
 * (the server-built snapshot: the whole universe in ONE request, matched
 * client-side), `resolveSchool` / `searchSchools` → `/api/hubspot/schools`.
 *
 * ⚠️ IT USED TO CALL A ROUTE THAT DOES NOT EXIST. `GET /api/championships/search`
 * 404s — measured on the live site by instrumenting `window.fetch` — and the
 * helper it went through swallowed that and fell back to six hard-coded DEMO
 * records, two of them fictional HIGH SCHOOLS. So the level could either fail
 * to match anything real or, worse, seed a recordable build from invented data.
 * There is no demo path on this surface now; see `conceptSources.js`.
 */
export default function ConceptBrief({ base, onCompose, composed, onReset }) {
  const [text, setText] = useState('')
  const [state, setState] = useState('idle')   // idle · working · choose · error
  const [candidates, setCandidates] = useState([])
  const [err, setErr] = useState('')
  const abortRef = useRef(null)

  const brief = useMemo(() => parseBrief(text), [text])

  /* Resolve the line → records → a whole build. */
  const run = useCallback(async (pick = null) => {
    if (brief.empty) return
    abortRef.current?.abort?.()
    const ac = new AbortController()
    abortRef.current = ac
    setState('working')
    setErr('')
    try {
      let championship = pick
      if (!championship) {
        /* THE REAL UNIVERSE — one request, 14,797 records, matched here.
         * There is no demo fallback on this path by design: a fabricated
         * record must never be able to seed a recordable build. */
        const ranked = await findChampionships(brief, ac.signal)
        const match = resolveMatch(ranked)
        if (match.state === 'none') {
          setState('error')
          setErr('Nothing in the championship universe matches that line. Try the governing body and the sport as they would be written on the record — for example “ACC men’s basketball”.')
          return
        }
        if (match.state === 'choose') {
          /* THE ONE QUESTION. */
          setCandidates(match.candidates)
          setState('choose')
          return
        }
        championship = match.choice
      }

      /* The school carries the two things the operator is not being asked for:
       * the OWNED crest and the verified brand colours. A conference record
       * names no school, so the brief is the other half of this lookup. */
      const found = await resolveSchool({ brief, championship, signal: ac.signal })
      if (ac.signal.aborted) return
      const school = found.school
      const out = composeConcept({ base, brief, championship, school })
      onCompose({
        ...out,
        championship,
        school,
        schoolLookup: found,
        blockers: conceptBlockers({ build: out.build, school: school || found.nearest, schoolLookup: found }),
      })
      setState('idle')
      setCandidates([])
    } catch (e) {
      if (ac.signal.aborted) return
      setState('error')
      setErr(e?.message || 'The lookup failed. Try again.')
    }
  }, [brief, base, onCompose])

  /* A correction goes back through the same writer the composer used, and the
   * receipt is re-stamped so the source reads as the operator, not the record
   * it no longer matches. */
  const edit = useCallback((path, value) => {
    if (!composed) return
    onCompose({
      ...composed,
      build: applyReceipt(composed.build, path, value),
      receipts: composed.receipts.map((r) => (r.path === path ? { ...r, value, source: 'you corrected it', edited: true } : r)),
      blockers: conceptBlockers({ build: applyReceipt(composed.build, path, value), school: composed.school, schoolLookup: composed.schoolLookup }),
    })
  }, [composed, onCompose])

  /* KEYING A FIELD NO RECORD ANSWERED.
   *
   * Distinct from `edit` above, and the distinction is the receipt: `edit`
   * replaces a value a record DID answer and stamps it "you corrected it";
   * this one is the ONLY source a presentation line or an audience has, so it
   * stamps "you keyed it" and appends a receipt where there was none.
   *
   * Clearing it REMOVES the receipt rather than leaving a blank one — a field
   * the operator did not key must be OMITTED from the payload, not blanked,
   * which is the same rule `buildPayload.js` enforces on every keyed string. */
  const keyField = useCallback((path, label, value) => {
    if (!composed) return
    const v = String(value)
    const build = applyReceipt(composed.build, path, v)
    const has = composed.receipts.some((r) => r.path === path)
    let receipts
    if (!v.trim()) {
      receipts = composed.receipts.filter((r) => r.path !== path)
    } else if (has) {
      receipts = composed.receipts.map((r) => (r.path === path ? { ...r, value: v, source: SOURCE_KEYED, edited: true } : r))
    } else {
      receipts = [...composed.receipts, { id: path, path, label, value: v, source: SOURCE_KEYED, note: '' }]
    }
    onCompose({
      ...composed,
      build,
      receipts,
      blockers: conceptBlockers({ build, school: composed.school, schoolLookup: composed.schoolLookup }),
    })
  }, [composed, onCompose])

  /* THE CREST, ATTACHED IN PLACE. The write goes through the same state every
   * other line does, and `conceptBlockers` is re-run against the result — so
   * the blocker clears because the build genuinely has an owned file on it, not
   * because a control said it did. */
  const setCrest = useCallback((ref) => {
    if (!composed) return
    const build = { ...composed.build, logoC: ref }
    const receipts = [
      ...composed.receipts.filter((r) => r.path !== 'logoC.url'),
      {
        id: 'logoC.url',
        path: 'logoC.url',
        label: 'Client crest',
        value: ref.url,
        source: SOURCE_BRAND_KIT,
        note: 'The real owned file, injected by the locked layer. No model draws this.',
      },
    ]
    onCompose({ ...composed, build, receipts, blockers: conceptBlockers({ build, school: composed.school, schoolLookup: composed.schoolLookup }) })
  }, [composed, onCompose])

  /* ------------------------------------------------------------------ *
   * THE SCHOOL SWITCH — why this exists, in one place.
   *
   * The crest is the one sourced value that CANNOT be typed: it is a real
   * owned file or it is nothing. And the school search ranks three crest-less
   * HIGH SCHOOLS above "Duke University" on the bare token "Duke" (measured
   * live — see conceptSources.js and BACKEND-HANDOVER §9). So the concept has
   * to be able to say "I landed here, here is what else there was, switch it"
   * — which is the difference between a level that works today and one that
   * waits on a backend fix.
   * ------------------------------------------------------------------ */
  const [schoolQ, setSchoolQ] = useState('')
  const [schoolHits, setSchoolHits] = useState(null)
  const [schoolBusy, setSchoolBusy] = useState(false)

  const runSchoolSearch = useCallback(async (q) => {
    if (!q.trim()) return
    setSchoolBusy(true)
    setErr('')
    try { setSchoolHits(await searchSchools(q)) }
    catch (e) { setSchoolHits(null); setErr(e?.message || 'School search unavailable. Try again.'); setState('error') }
    finally { setSchoolBusy(false) }
  }, [])

  const chooseSchool = useCallback((school) => {
    if (!composed) return
    const out = composeConcept({
      base, brief, championship: composed.championship, school,
    })
    onCompose({
      ...out,
      championship: composed.championship,
      school,
      schoolLookup: { ...(composed.schoolLookup || {}), school, reason: school.logoUrl ? 'ok' : 'no_owned_crest', picked: true },
      blockers: conceptBlockers({ build: out.build, school }),
    })
    setSchoolHits(null)
    setSchoolQ('')
  }, [composed, base, brief, onCompose])

  return (
    <section className={styles.wrap}>
      {/* -------- the one input -------- */}
      <div className={`card ${styles.briefCard}`}>
        <label className="field-label" htmlFor="concept-brief">Describe the job in one line</label>
        <textarea
          id="concept-brief"
          className={styles.brief}
          rows={2}
          value={text}
          placeholder="A championship ring for Duke Basketball 2027 ACC Champions"
          onChange={(e) => setText(e.target.value)}
        />
        <p className={styles.promise}>{CONCEPT_PROMISE}</p>
        <div className={styles.briefActions}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={brief.empty || state === 'working'}
            onClick={() => run(null)}
          >
            {state === 'working' ? 'Sourcing…' : composed ? 'Source it again' : 'Source the build'}
          </button>
          {composed && (
            <button type="button" className="btn" onClick={() => { setText(''); onReset?.() }}>
              Start over
            </button>
          )}
        </div>
        {state === 'error' && <p className={styles.err}>{err}</p>}

        {/* THE OTHER DOOR TO THE SAME JOB, in the SAME WORDS the AI Tools page
            uses — rendered from `lib/aiTools.js#STUDIO_RELATIONSHIP` rather
            than paraphrased, so a rep is never left deciding between what look
            like two rival products. */}
        <p className={styles.relationship}>
          {STUDIO_RELATIONSHIP} <Link to="/ai-tools">AI Tools</Link>
        </p>
      </div>

      {/* -------- THE ONE QUESTION -------- */}
      {state === 'choose' && (
        <div className={`card ${styles.chooseCard}`}>
          <div className="field-label">Which one?</div>
          <p className={styles.note}>
            More than one record fits that line closely enough that picking for you could put the
            wrong division on the ring. This is the only question.
          </p>
          <ul className={styles.choices}>
            {candidates.map((c) => (
              <li key={c.record.id || c.record.name}>
                <button type="button" className={styles.choice} onClick={() => run(c.record)}>
                  <span className={styles.choiceName}>{c.record.name || c.record.championship}</span>
                  <span className={styles.choiceMeta}>
                    {[c.record.sport, c.record.level, c.record.state, c.record.year].filter(Boolean).join(' · ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* -------- the receipt -------- */}
      {composed && (
        <div className={`card ${styles.receiptCard}`}>
          <div className="field-label">What it sourced — and where each line came from</div>
          <p className={styles.note}>
            Nothing here was invented. Correct any line before you render; a field no record
            answered was left out rather than guessed.
          </p>
          <ul className={styles.receipts}>
            {composed.receipts.map((r) => (
              <li key={r.id} className={styles.receipt}>
                <label className={styles.rLabel} htmlFor={`r-${r.id}`}>{r.label}</label>
                <input
                  id={`r-${r.id}`}
                  className={styles.rInput}
                  value={r.value}
                  onChange={(e) => edit(r.path, e.target.value)}
                />
                <span className={`${styles.rSource} ${r.edited ? styles.rEdited : ''}`}>{r.source}</span>
                {r.note && <span className={styles.rNote}>{r.note}</span>}
              </li>
            ))}
          </ul>

          {/* -------- THE PRESENTATION — KEYED, NEVER PICKED, NEVER AI --------
              Brian: *"there is a presentation line and audience but theres
              nowhere to input this."* Both are real keyed fields in the payload
              and both are warned about by `validateStudio()`; in this level
              there was no control for either. `audience` is sourced from
              `ch.owner || ch.audience`, which no championship record carries,
              so it could never appear at all.

              The suggestion is a CHIP carrying its own source and writes
              nothing until it is clicked — suggest-don't-autofill, on the level
              that has no steps. A field left empty is omitted from the payload,
              not blanked. */}
          {composed.keyable?.length > 0 && (
            <div className={styles.keyBox}>
              <div className="field-label">The presentation — no record answered these, so key them</div>
              <p className={styles.note}>
                Typed, never picked and never drawn. Leave one blank and it is left out of the
                build rather than sent as an empty line.
              </p>
              <ul className={styles.receipts}>
                {composed.keyable.map((f) => {
                  const value = String(
                    (composed.build.keyed && composed.build.keyed[f.path.split('.')[1]]) || ''
                  )
                  return (
                    <li key={f.path} className={styles.receipt}>
                      <label className={styles.rLabel} htmlFor={`k-${f.path}`}>{f.label}</label>
                      <input
                        id={`k-${f.path}`}
                        className={styles.rInput}
                        value={value}
                        placeholder={f.label === 'Presented to' ? 'e.g. the Athletic Director' : 'e.g. Duke University – Basketball – ACC Championship – 2027'}
                        onChange={(e) => keyField(f.path, f.label, e.target.value)}
                      />
                      <span className={styles.rNote}>{f.hint}</span>
                      {f.suggestion && f.suggestion !== value && (
                        <button
                          type="button"
                          className={styles.suggest}
                          onClick={() => keyField(f.path, f.label, f.suggestion)}
                        >
                          Use: {f.suggestion}
                          <span className={styles.suggestSrc}>{f.suggestionSource}</span>
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* THE SCHOOL, ALWAYS VISIBLE AND ALWAYS SWITCHABLE. */}
          <div className={styles.schoolBox}>
            <div className="field-label">School — the owned crest comes from this record</div>
            {composed.school ? (
              <div className={styles.schoolRow}>
                <img className={styles.crest} src={composed.school.logoUrl} alt="" width="34" height="34" />
                <span className={styles.schoolName}>{composed.school.name}</span>
                <span className={styles.schoolOk}>owned crest on file</span>
              </div>
            ) : (
              <p className={styles.note}>
                {composed.schoolLookup?.reason === 'ambiguous_school'
                  ? <>Several school records match. Search below and choose the correct customer. Crest availability does not decide school identity.</>
                  : composed.schoolLookup?.reason === 'identity_mismatch'
                    ? <>The returned records do not match the customer’s school identity. Search below and choose the correct school before rendering.</>
                  : composed.schoolLookup?.nearest
                  ? <>The closest record is <strong>{composed.schoolLookup.nearest.name}</strong>, and it has <strong>no owned crest on file</strong>. Search for the right school below, or add the mark in the Marks Library.</>
                  : <>No school record matched this job. Search for it below.</>}
              </p>
            )}

            <div className={styles.schoolSearch}>
              <input
                className={styles.rInput}
                value={schoolQ}
                placeholder="Search schools — e.g. Duke University"
                onChange={(e) => setSchoolQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSchoolSearch(schoolQ) } }}
              />
              <button type="button" className="btn" onClick={() => runSchoolSearch(schoolQ)} disabled={schoolBusy || !schoolQ.trim()}>
                {schoolBusy ? 'Searching…' : 'Search'}
              </button>
            </div>

            {schoolHits && (
              schoolHits.length ? (
                <ul className={styles.choices}>
                  {schoolHits.map((h) => (
                    <li key={h.id}>
                      <button type="button" className={styles.choice} onClick={() => chooseSchool(h)}>
                        <span className={styles.choiceName}>{h.name}</span>
                        <span className={styles.choiceMeta}>
                          {[h.location, h.logoUrl ? 'owned crest ✓' : 'no owned crest'].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p className={styles.note}>No school records matched that search.</p>
            )}
          </div>

          {composed.blockers?.length > 0 && (
            <ul className={styles.blockers}>
              {composed.blockers.map((b) => (
                <li key={b.code} className={styles.blocker}>
                  <strong>{b.what}</strong>
                  <span>{b.fix}</span>
                  {/* THE BLOCKER CARRIES THE ACTION. A rep who hits this can
                      resolve it here; before, it named the Marks Library and
                      left them to navigate — and for a high school that queue
                      has nothing staged to approve, so it was a dead end. The
                      refusal is unchanged: the build stays blocked until a real
                      owned file is attached. */}
                  {b.code === 'no_owned_crest' && (
                    <ConceptCrest
                      schoolName={composed.school?.name || composed.schoolLookup?.nearest?.name || ''}
                      onCrest={setCrest}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

